import React from 'react';
import { createPortal } from 'react-dom';
import { Images, Lock, ShieldCheck, User, X } from 'lucide-react';
import { parseISO } from 'date-fns';
import { BeltPromotion, CheckInPhoto, GradeInfo } from 'app/javascript/types';
import { MONTH_LABELS_PT_SHORT, formatBrDate, parseIsoDateParts } from 'app/javascript/utils/dates';
import {
  ADULT_RANKS,
  BeltRankValue,
  KIDS_RANKS,
  getBeltLabel,
  getBeltSortOrder,
  getBeltTheme,
  getMaxGrades,
  normalizeBeltRank,
} from 'app/javascript/services/belts';
interface TimelineProps {
  promotions: BeltPromotion[];
}

const monthFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'short' });

function formatTimelineDate(dateStr: string): string {
  const date = parseISO(dateStr);
  const month = monthFormatter.format(date).replace('.', '').toUpperCase();
  return `${month} ${date.getFullYear()}`;
}

const TimelineNode: React.FC<{ isActive: boolean }> = ({ isActive }) =>
  isActive ? (
    <div className="z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-4 border-background bg-yellow-400 shadow-[0_0_12px_rgba(250,204,21,0.4)]">
      <span className="size-2 rounded-full bg-slate-950" />
    </div>
  ) : (
    <div className="z-10 flex size-8 shrink-0 items-center justify-center rounded-lg border-2 border-border bg-secondary">
      <span className="size-2 rounded-full bg-muted-foreground" />
    </div>
  );

const NextBeltNode: React.FC = () => (
  <div className="relative z-10 flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-card">
    <div
      className="absolute inset-[-2px] animate-border-spin"
      style={{
        background:
          'conic-gradient(from 0deg, transparent 60%, rgba(250,204,21,0.6) 80%, rgba(250,204,21,0.8) 90%, transparent 100%)',
      }}
    />
    <div className="absolute inset-[2px] rounded-md bg-card" />
    <Lock className="relative z-10 size-3 text-yellow-400/60" />
  </div>
);

const FutureNode: React.FC = () => (
  <div className="z-10 flex size-8 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-border bg-card">
    <Lock className="size-3 text-muted-foreground" />
  </div>
);

const PhotoTimelineNode: React.FC = () => (
  <div className="z-10 flex size-8 shrink-0 items-center justify-center rounded-lg border border-yellow-400/30 bg-slate-900 shadow-[0_0_12px_rgba(250,204,21,0.18)]">
    <Images className="size-3.5 text-yellow-400/80" />
  </div>
);

// Adult belts are sequential and never skipped, so they're always projected in full as the
// long-term path. Kids belts, on the other hand, can be skipped depending on age — projecting
// the whole kids ladder both clutters the timeline and is misleading. So for a kid we only
// show the single next kids belt, followed by the adult belts as the long-term goal.
function getFutureBelts(earnedRanks: BeltRankValue[]): BeltRankValue[] {
  if (earnedRanks.length === 0) return [];

  const earnedSet = new Set(earnedRanks);
  const highestOrder = Math.max(...earnedRanks.map((r) => getBeltSortOrder(r)));
  const isUnearnedAhead = (r: BeltRankValue) =>
    !earnedSet.has(r) && getBeltSortOrder(r) > highestOrder;

  const futureAdult = ADULT_RANKS.filter(isUnearnedAhead);

  const hasKidsBelt = earnedRanks.some((r) => KIDS_RANKS.includes(r));
  if (!hasKidsBelt) return futureAdult;

  // KIDS_RANKS is ordered ascending, so find() returns the immediate next kids belt.
  const nextKidsBelt = KIDS_RANKS.find(isUnearnedAhead);
  return nextKidsBelt ? [nextKidsBelt, ...futureAdult] : futureAdult;
}

// Belt visualization geometry. Each card is rendered as a horizontal BJJ belt:
//
//   |20px solid tip|--------- body color ---------|=== black/red section ===|tip|
//   ^               ^                              ^                         ^
//   border-left    text overlay area              graus stripes              right tip
//                   (constrained to BELT_BLACK_START)
//
// BELT_BLACK_START/END are percentages of card width. The graus stripes
// protrude 5px above/below the black section (overflow into body color)
// to mimic the "esparadrapo" look on real belts.
//
// The section is sized for the rank's maximum graus, not the ones earned, so
// it doesn't change as stripes are sewn: four fit in 24% of the card, the black
// belt (six) gets 42%.
const BELT_BLACK_END = 94;
const BELT_BLACK_START_DEFAULT = 70;
const BELT_BLACK_START_SIX_GRAUS = 52;

function beltBlackStart(maxStripes: number): number {
  return maxStripes > 4 ? BELT_BLACK_START_SIX_GRAUS : BELT_BLACK_START_DEFAULT;
}

interface BeltStripesProps {
  grades?: GradeInfo[];
  max: number;
}

function formatStripeDate(dateStr?: string): string | null {
  if (!dateStr) return null;
  const parts = parseIsoDateParts(dateStr);
  if (!parts) return null;
  return `${MONTH_LABELS_PT_SHORT[parts.m - 1]}/${parts.y}`;
}

const BeltStripes: React.FC<BeltStripesProps> = ({ grades, max }) => {
  if (max === 0) return null;
  const sorted = (grades || []).slice().sort((a, b) => a.number - b.number);
  const visible = Math.min(sorted.length, max);

  return (
    <div className="pointer-events-none absolute left-0 right-0 flex items-stretch justify-evenly px-1.5"
         style={{ top: '-5px', bottom: '-5px' }}>
      {Array.from({ length: max }).map((_, i) => {
        const filled = i < visible;
        const label = filled ? formatStripeDate(sorted[i]?.date) : null;
        return (
          <div
            key={i}
            className="mx-[2px] flex min-w-0 max-w-[20px] flex-1 items-center justify-center rounded-[2px]"
            style={{
              background: filled ? '#ffffff' : 'transparent',
              boxShadow: filled ? '0 0 0 1px rgba(0,0,0,0.35)' : 'none',
            }}
          >
            {label && (
              <span
                className="font-mono font-semibold leading-none text-slate-900"
                style={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  fontSize: '10px',
                  letterSpacing: '0.02em',
                }}
              >
                {label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface BeltRowProps {
  rank: BeltRankValue;
  grades?: GradeInfo[];
  label: string;
  subtitle: string | null;
  dateStr: string | null;
  emphasized?: boolean;
  dimmed?: boolean;
  validatedBadge?: React.ReactNode;
  instructorRow?: React.ReactNode;
  footer?: React.ReactNode;
}

const BeltRow: React.FC<BeltRowProps> = ({
  rank,
  grades,
  label,
  subtitle,
  dateStr,
  emphasized,
  dimmed,
  validatedBadge,
  instructorRow,
  footer,
}) => {
  const theme = getBeltTheme(rank);
  const bodyColor = theme.hex;
  const tipColor = theme.borderHex;
  const isBlackBelt = rank === 'black';
  const sectionColor = isBlackBelt ? '#b91c1c' : '#0a0a0a';
  const maxStripes = getMaxGrades(rank);
  const blackStart = beltBlackStart(maxStripes);

  const onLight = theme.isLight;
  const titleColor = onLight ? 'text-slate-900' : 'text-white';
  const subtitleColor = onLight ? 'text-slate-700' : 'text-white/85';

  return (
    <div
      className={`relative rounded-xl shadow-lg ${emphasized ? 'ring-1 ring-yellow-400/40' : ''}`}
      style={{
        opacity: dimmed ? 0.45 : 1,
        borderLeftWidth: '20px',
        borderLeftStyle: 'solid',
        borderLeftColor: tipColor,
      }}
    >
      {/* Belt body background — rounded so corners stay visually rounded; overflow stays visible so stripes can protrude */}
      <div
        className={`relative ${footer || instructorRow ? 'rounded-tr-xl' : 'rounded-r-xl'}`}
        style={{ background: bodyColor, minHeight: '5.5rem' }}
      >
        {/* Subtle inner gradient for depth on the body */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(0,0,0,0.15) 100%)',
          }}
        />

        {/* Black/red section (graus area) — 0.5px inset so it sits inside the belt body */}
        <div
          className="absolute"
          style={{
            top: '0.5px',
            bottom: '0.5px',
            left: `${blackStart}%`,
            right: `${100 - BELT_BLACK_END}%`,
            background: sectionColor,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.4)',
          }}
        >
          <BeltStripes grades={grades} max={maxStripes} />
        </div>

        {/* Right body-color tip */}
        <div
          aria-hidden
          className="absolute inset-y-0 right-0"
          style={{ width: `${100 - BELT_BLACK_END}%`, background: bodyColor }}
        />

        {/* Content overlay */}
        <div className="relative flex h-full items-center px-5 py-4" style={{ maxWidth: `${blackStart}%` }}>
          <div className="min-w-0">
            <h4 className={`font-bebas text-2xl uppercase leading-none tracking-wide ${titleColor}`}>
              {label}
            </h4>
            {dateStr && (
              <p className={`mt-1 font-mono text-xs ${subtitleColor}`}>{dateStr}</p>
            )}
            {subtitle && (
              <p className={`mt-1 text-xs font-bold uppercase ${subtitleColor}`}>{subtitle}</p>
            )}
            {validatedBadge && <div className="mt-1.5">{validatedBadge}</div>}
          </div>
        </div>
      </div>

      {(instructorRow || footer) && (
        <div className="space-y-3 rounded-br-xl border-t border-border/50 bg-secondary/40 p-4 backdrop-blur-sm">
          {instructorRow}
          {footer}
        </div>
      )}
    </div>
  );
};

const CheckInPhotoMontage: React.FC<{ photos: CheckInPhoto[]; title: string; rank: BeltRankValue }> = ({ photos, title, rank }) => {
  const [selectedPhoto, setSelectedPhoto] = React.useState<CheckInPhoto | null>(null);
  const theme = getBeltTheme(rank);
  const headerTextColor = theme.isLight ? '#0f172a' : '#f8fafc';

  React.useEffect(() => {
    if (!selectedPhoto) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedPhoto(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto]);

  return (
    <>
      <div
        className="mb-3 w-full overflow-hidden rounded-xl border bg-background/45 shadow-lg shadow-black/10"
        style={{ borderColor: `${theme.borderHex}99` }}
      >
        <div
          className="border-b px-3 py-2"
          style={{
            borderColor: `${theme.borderHex}80`,
            background: `linear-gradient(90deg, ${theme.hex}, ${theme.borderHex})`,
          }}
        >
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: headerTextColor }}>
            {title}
          </p>
        </div>
        <div className="grid max-h-72 grid-cols-5 overflow-y-auto scroll-smooth md:max-h-96 md:grid-cols-6">
          {photos.map((photo) => (
            <button
              key={photo.id}
              type="button"
              className="group relative aspect-square border border-transparent bg-secondary text-left transition hover:z-10 hover:border-yellow-400/80 hover:shadow-[0_0_14px_rgba(250,204,21,0.24)] focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/70"
              aria-label={`Abrir foto do treino em ${formatBrDate(photo.classDate)}`}
              onClick={() => setSelectedPhoto(photo)}
            >
              <img
                src={photo.thumbnailUrl}
                alt={`Treino em ${formatBrDate(photo.classDate)}`}
                loading="lazy"
                decoding="async"
                className="size-full object-cover"
              />
              <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950/80 to-transparent px-2 py-1 font-mono text-[10px] text-white/90 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
                {formatBrDate(photo.classDate)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {selectedPhoto && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={`Foto do treino em ${formatBrDate(selectedPhoto.classDate)}`}
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-950 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="absolute right-3 top-3 z-10 rounded-full border border-white/15 bg-slate-950/80 p-2 text-slate-300 backdrop-blur transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/70"
              aria-label="Fechar foto"
              onClick={() => setSelectedPhoto(null)}
            >
              <X className="size-5" />
            </button>
            <img
              src={selectedPhoto.photoUrl}
              alt={`Treino em ${formatBrDate(selectedPhoto.classDate)}`}
              className="max-h-[82vh] w-full object-contain"
            />
            <div className="border-t border-slate-800 px-4 py-3 font-mono text-xs text-slate-400">
              {formatBrDate(selectedPhoto.classDate)}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const Timeline: React.FC<TimelineProps> = ({ promotions }) => {
  const sortedPromotions = [...promotions].sort((a, b) => getBeltSortOrder(b.rank) - getBeltSortOrder(a.rank));

  const earnedRanks = promotions.map((p) => normalizeBeltRank(p.rank));
  // A black belt climbs degree by degree for years: what lies ahead is the next
  // degree, and the coral belts only come into view once all six are earned.
  const current = sortedPromotions[0];
  const earnedDegrees = current?.grades?.length || 0;
  const nextDegree =
    current && normalizeBeltRank(current.rank) === 'black' && earnedDegrees < getMaxGrades('black')
      ? earnedDegrees + 1
      : null;
  const futureBelts = nextDegree ? [] : getFutureBelts(earnedRanks);
  const futureCount = futureBelts.length + (nextDegree ? 1 : 0);

  return (
    <div className="mx-auto">
      <div className="relative flex flex-col gap-6 pl-4">
        <div className="absolute bottom-0 left-8 top-0 w-px bg-gradient-to-b from-yellow-400 via-border to-transparent" />

        {nextDegree && current ? (
          <div
            key={`future-degree-${nextDegree}`}
            className="relative flex animate-timeline-enter items-stretch opacity-0"
          >
            <NextBeltNode />

            <div className="ml-8 flex-1">
              <BeltRow
                rank="black"
                // The preview only counts stripes: no dates, those stay on the belt held.
                grades={Array.from({ length: nextDegree }, (_, i) => ({ id: `next-${i + 1}`, number: i + 1, date: '' }))}
                label={`Faixa ${getBeltLabel(current.rank)} · ${nextDegree}º grau`}
                subtitle="Próximo grau"
                dateStr={null}
                dimmed
                emphasized
              />
            </div>
          </div>
        ) : null}

        {[...futureBelts].reverse().map((rank, index, arr) => {
          const beltLabel = getBeltLabel(rank);
          const isNextBelt = index === arr.length - 1;
          const subtitle = isNextBelt ? 'Próxima faixa' : null;

          return (
            <div
              key={`future-${rank}`}
              className="relative flex animate-timeline-enter items-stretch opacity-0"
              style={{ animationDelay: `${index * 120}ms` }}
            >
              {isNextBelt ? <NextBeltNode /> : <FutureNode />}

              <div className="ml-8 flex-1">
                <BeltRow
                  rank={rank}
                  label={`Faixa ${beltLabel}`}
                  subtitle={subtitle}
                  dateStr={null}
                  dimmed
                  emphasized={isNextBelt}
                />
              </div>
            </div>
          );
        })}

        {sortedPromotions.map((promo, index) => {
          const beltLabel = getBeltLabel(promo.rank);
          const isActive = index === 0;
          const isWhite = promo.rank === 'white';
          const hasValidStart = promo.date && !isNaN(new Date(promo.date).getTime());
          // End date = when the next-up belt was received (sortedPromotions is
          // descending, so index-1 holds the newer belt). Active belt has no end.
          const newerPromo = !isActive ? sortedPromotions[index - 1] : null;
          const hasValidEnd =
            newerPromo?.date && !isNaN(new Date(newerPromo.date).getTime());
          const startStr = hasValidStart ? formatTimelineDate(promo.date) : null;
          const endStr = hasValidEnd ? formatTimelineDate(newerPromo!.date) : null;
          const dateStr = startStr
            ? endStr
              ? `${startStr} — ${endStr}`
              : startStr
            : null;
          const gradeCount = promo.grades?.length || 0;

          const subtitle = isActive
            ? promo.instructorName || null
            : isWhite
              ? 'Início da Jornada'
              : promo.instructorName || null;

          const instructorRow =
            !isActive && !isWhite && gradeCount > 0 && promo.instructorName ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <User className="size-3 shrink-0" />
                <span>{promo.instructorName}</span>
              </div>
            ) : null;

          const validatedBadge = promo.isValidated ? (
            <div
              className={`flex items-center gap-1.5 font-semibold uppercase tracking-wide ${
                isActive ? 'text-[11px] text-emerald-400' : 'text-[10px] text-emerald-400/60'
              }`}
            >
              <ShieldCheck className={isActive ? 'size-3.5' : 'size-3'} />
              Verificada
            </div>
          ) : null;

          const checkInPhotos = promo.checkInPhotos || [];
          const footer =
            promo.photoUrl || promo.notes ? (
              <div className="flex flex-col gap-2">
                {promo.photoUrl && (
                  <div className="h-32 w-full overflow-hidden rounded-lg">
                    <img src={promo.photoUrl} alt="Graduação" className="size-full object-cover" />
                  </div>
                )}
                {promo.notes && (
                  <p className="text-sm italic leading-relaxed text-muted-foreground">
                    &ldquo;{promo.notes}&rdquo;
                  </p>
                )}
              </div>
            ) : null;

          return (
            <React.Fragment key={promo.id}>
              {checkInPhotos.length > 0 && (
                <div
                  className="relative flex animate-timeline-enter items-stretch"
                  style={{ animationDelay: `${(futureCount + index) * 120}ms` }}
                >
                  <PhotoTimelineNode />

                  <div className="ml-8 flex-1">
                    <CheckInPhotoMontage
                      photos={checkInPhotos}
                      title={`Treinos registrados na faixa ${beltLabel.toLowerCase()}`}
                      rank={normalizeBeltRank(promo.rank)}
                    />
                  </div>
                </div>
              )}

              <div
                className="relative flex animate-timeline-enter items-stretch"
                style={{ animationDelay: `${(futureCount + index) * 120}ms` }}
              >
                <TimelineNode isActive={isActive} />

                <div className="ml-8 flex-1">
                  <BeltRow
                    rank={normalizeBeltRank(promo.rank)}
                    grades={promo.grades}
                    label={`Faixa ${beltLabel}`}
                    subtitle={subtitle}
                    dateStr={dateStr}
                    emphasized={isActive}
                    instructorRow={instructorRow}
                    validatedBadge={validatedBadge}
                    footer={footer}
                  />
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;
